import ReactDOM from 'react-dom/client'
import { App } from './App'
import type { AppProps, BillingMode, MicroInfo, OpenMiaProps, OpenNitiaCuotaProps } from './App'
import { SITE_ENV, type Theme, type Themes, type TopbarMenu, type Delegations } from './common'
import { buildQuery, getTokens } from './utils/requests'
import pkg from '../package.json'

const JSONParse = (data: string | undefined) => {
  try {
    return JSON.parse(data as string)
  } catch (e) {
    return undefined
  }
}

const EXTRA_CONSUMPTION_CODE = 'nitia.addon.extra.monthly'

let miaLoadPromise: Promise<void> | null = null

function loadMia (url: string): Promise<void> {
  if (miaLoadPromise) return miaLoadPromise
  miaLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      miaLoadPromise = null
      reject(new Error(`Failed to load MIA script from ${url}`))
    })
    document.head.appendChild(script)
  })
  return miaLoadPromise
}

class FarmacloudHeaderBar extends HTMLElement {
  props: AppProps
  renderer: any

  constructor() {
    super();
    this.props = this.getPropsFromDataset()
    this.attachShadow({ mode: 'open' })
  }

  connectedCallback() {
    console.log('farmacloud-bar: ', pkg.version)
    this.props = this.getPropsFromDataset()
    this.render()
  }

  render () {
    this.renderer = this.renderer || ReactDOM.createRoot(this.shadowRoot as ShadowRoot)
    this.renderer.render(<App {...this.props} onOpenMia={(props) => this.openTopbarIa(props)} />)
  }

  getPropsFromDataset () {
    return {
      hide: this.dataset.initialstate !== 'show',
      env: SITE_ENV,
      token: undefined,
      freeText1: this.dataset.freetext1 || undefined,
      freeText2: this.dataset.freetext2 || undefined,
      showDateTime: this.dataset.datetime === 'true',
      showHelp: this.dataset.help === 'true',
      showConfiguration: this.dataset.config === 'true',
      showLocker: this.dataset.locker === 'true',
      showFindEngine: this.dataset.findengine === 'true',
      showFindEngineTextBox: this.dataset.findenginetextbox === 'true',
      showShoppingCart: this.dataset.shoppingcart === 'true',
      shoppingCartItems: parseInt(this.dataset.shoppingcartitems || '0'),
      showFavorites: this.dataset.favorites === 'true',
      showNitiaButton: this.dataset.nitiabutton?.trim().toLowerCase() !== 'false',
      showSwitchTheme: this.dataset.switchtheme === 'true',
      encodeFindEngineText: this.dataset.encodefindenginetext === 'true',
      primaryColor: this.dataset.primarycolor || undefined,
      theme: (this.dataset.theme as Theme) || ('dark' as Theme),
      dropdownMenu: JSONParse(this.dataset.dropdownmenu),
      delegations: JSONParse(this.dataset.delegations),
      secured: this.dataset.secured === 'true',
      state: this.dataset.state
    }
  }

  // PUBLIC METHODS

  hide (): void {
    this.props.hide = true
    this.render()
  }

  show (): void {
    this.props.hide = false
    this.render()
  }

  reload (): void {
    this.render()
  }

  logout (): void {
    window.dispatchEvent(new CustomEvent('onLogout', { composed: true }))
    this.props.token = undefined
    this.render()
  }

  setToken (token: { token: string }): void {
    // with the refactor the token is no longer necessary but serves as a backup plan until we are done working on it
    this.props.token = token.token
    this.render()
  }

  setFreeText1 (freeText1: string): void {
    this.props.freeText1 = freeText1
    this.render()
  }

  removeFreeText1 (): void {
    this.props.freeText1 = undefined
    this.render()
  }

  setFreeText2 (freeText2: string): void {
    this.props.freeText2 = freeText2
    this.render()
  }

  removeFreeText2 (): void {
    this.props.freeText2 = undefined
    this.render()
  }

  setShowDateTime (showDate: boolean): void {
    if (this.props.showDateTime === showDate) return
    this.props.showDateTime = showDate
    this.render()
  }

  setHelp (showHelp: boolean): void {
    if (this.props.showHelp === showHelp) return
    this.props.showHelp = showHelp
    this.render()
  }

  setSwitchTheme (showSwitchTheme: boolean): void {
    if (this.props.showSwitchTheme === showSwitchTheme) return
    this.props.showSwitchTheme = showSwitchTheme
    this.render()
  }

  setConfig (showConfiguration: boolean): void {
    if (this.props.showConfiguration === showConfiguration) return
    this.props.showConfiguration = showConfiguration
    this.render()
  }

  setLocker (showLocker: boolean): void {
    if (this.props.showLocker === showLocker) return
    this.props.showLocker = showLocker
    this.render()
  }

  setFindEngine (showFindEngine: boolean): void {
    if (this.props.showFindEngine === showFindEngine) return
    this.props.showFindEngine = showFindEngine
    this.render()
  }

  setFindEngineTextBox (showFindEngineTextBox: boolean): void {
    if (this.props.showFindEngineTextBox === showFindEngineTextBox) return
    this.props.showFindEngineTextBox = showFindEngineTextBox
    this.render()
  }

  setEncodeFindEngineText (encodeFindEngineText: boolean): void {
    if (this.props.encodeFindEngineText === encodeFindEngineText) return
    this.props.encodeFindEngineText = encodeFindEngineText
    this.render()
  }

  setShoppingCart (showShoppingCart: boolean): void {
    if (this.props.showShoppingCart === showShoppingCart) return
    this.props.showShoppingCart = showShoppingCart
    this.render()
  }

  setShoppingCartItems (shoppingCartItems: number): void {
    if (this.props.shoppingCartItems === shoppingCartItems) return
    this.props.shoppingCartItems = Math.abs(shoppingCartItems)
    this.render()
  }

  setPrimaryColor (primaryColor: string): void {
    if (this.props.primaryColor === primaryColor) return
    this.props.primaryColor = primaryColor
    this.render()
  }

  setTheme (theme: Theme): void {
    this.props.theme = theme
    this.render()
  }

  setThemes (themes: Themes): void {
    this.props.themes = themes
    this.render()
  }

  setFavorites (showFavorites: boolean): void {
    if (this.props.showFavorites === showFavorites) return
    this.props.showFavorites = showFavorites
    this.render()
  }

  setNitiaButton (showNitiaButton: boolean): void {
    if (this.props.showNitiaButton === showNitiaButton) return
    this.props.showNitiaButton = showNitiaButton
    // Persisted to the attribute so a reconnect, which recomputes props from
    // the dataset, does not silently reveal a button the host hid.
    this.dataset.nitiabutton = String(showNitiaButton)
    this.render()
  }

  setDropdownMenu (data: TopbarMenu): void {
    this.props.dropdownMenu = data
    this.render()
  }

  setDelegations (delegations: Delegations): void {
    this.props.delegations = delegations
    this.render()
  }

  setSecured (secured: boolean): void {
    if (this.props.secured === secured) return
    this.props.secured = secured
    this.render()
  }

  setState (state: string): void {
    if (this.props.state === state) return
    this.props.state = state
    this.render()
  }

  setMicro (micro: MicroInfo): void {
    if (this.props.micro === micro) return
    this.props.micro = micro
    this.render()
  }

  async openBilling (
    appUuid: string,
    showCloseIcon: boolean = true,
    mode: BillingMode = 'manage-plans',
    extraData: Record<string, unknown> = {}
  ) {
    const tokens = await getTokens.call(this.props.token)
    this.setMicro({
      id: 'billing',
      baseUrl: process.env.REACT_APP_BILLING_BASE_URL as string,
      data: {
        app_uuid: appUuid,
        tokens: { ...tokens, jti: this.props.token },
        showCloseIcon,
        mode,
        ...extraData
      }
    })
  }

  async openNitiaCuota (appUuid: string, { showCloseIcon = true, spendNoticeAmount }: OpenNitiaCuotaProps = {}) {
    if (!appUuid) {
      console.warn('openNitiaCuota: appUuid is required')
      return
    }
    // Only the entity that offers the extra-consumption addon may open this modal
    const plans = await this.getBillingPlans(appUuid)
    const hasAddon = (plans?.addons || []).some(({ code }: { code: string }) => code === EXTRA_CONSUMPTION_CODE)
    if (!hasAddon) {
      console.warn('openNitiaCuota: the extra consumption addon is not available for this entity')
      return
    }
    return this.openBilling(
      appUuid,
      showCloseIcon,
      'hire-nitia-cuota',
      spendNoticeAmount === undefined ? {} : { spendNoticeAmount }
    )
  }

  async openTopbarIa ({ language = 'es' }: OpenMiaProps = {}) {
    try {
      await loadMia(process.env.REACT_APP_MIA_SCRIPT_URL as string)
    } catch (err) {
      console.error(err)
      return
    }
    window.MIA?.open({ jti: this.props.token, language })
  }

  async getBillingPlans (id: string, farmacloudUuid?: string) {
    const { token } = await getTokens.call(this.props.token)
    const query = buildQuery({ token: this.props.token, farmacloud_uuid: farmacloudUuid })
    return fetch(`${process.env.REACT_APP_GATEWAY_BASE}/billing/v1/entity/${id}/plans${query}`, {
      headers: {
        accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    }).then((res) => {
      return res.json()
    })
  }

  async getBillingSubscriptions (id: string, farmacloudUuid?: string) {
    const { token } = await getTokens.call(this.props.token)
    const query = buildQuery({ token: this.props.token, farmacloud_uuid: farmacloudUuid })
    return fetch(`${process.env.REACT_APP_GATEWAY_BASE}/billing/v1/entity/${id}/subscriptions${query}`, {
      headers: {
        accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    }).then((res) => {
      return res.json()
    })
  }

  closeBilling () {
    this.setMicro(null)
  }
}

window.customElements.define('farmacloud-header-bar', FarmacloudHeaderBar)
